import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import apiService from '../services/api';

const DashboardList = () => {
  const [dashboards, setDashboards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingDashboard, setEditingDashboard] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    created_by: 1
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const dashboardsResponse = await apiService.dashboards.getAll();
      setDashboards(dashboardsResponse.data);
    } catch (err) {
      setError('Failed to load data');
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingDashboard) {
        await apiService.dashboards.update(editingDashboard.id, {
          ...formData,
          updated_by: 1
        });
      } else {
        await apiService.dashboards.create(formData);
      }
      
      setShowForm(false);
      setEditingDashboard(null);
      setFormData({ name: '', created_by: 1 });
      fetchData();
    } catch (err) {
      setError('Failed to save dashboard');
      console.error('Error saving dashboard:', err);
    }
  };

  const handleEdit = (dashboard) => {
    setEditingDashboard(dashboard);
    setFormData({
      name: dashboard.name,
      created_by: dashboard.created_by || 1
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this dashboard?')) {
      try {
        await apiService.dashboards.delete(id);
        fetchData();
      } catch (err) {
        setError('Failed to delete dashboard');
        console.error('Error deleting dashboard:', err);
      }
    }
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingDashboard(null);
    setFormData({ name: '', created_by: 1 });
  };

  if (loading) {
    return <div className="loading">Loading dashboards...</div>;
  }

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Dashboards</h1>
        <button 
          className="btn btn-primary" 
          onClick={() => setShowForm(true)}
        >
          Add Dashboard
        </button>
      </div>

      {error && <div className="error">{error}</div>}

      {showForm && (
        <div className="form">
          <h3>{editingDashboard ? 'Edit Dashboard' : 'Add New Dashboard'}</h3>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="form-label">Dashboard Name *</label>
              <input
                type="text"
                className="form-control"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                required
              />
            </div>
            <div style={{display: 'flex', gap: '1rem'}}>
              <button type="submit" className="btn btn-primary">
                {editingDashboard ? 'Update' : 'Create'}
              </button>
              <button type="button" className="btn btn-secondary" onClick={resetForm}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="table-container">
        <table className="table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Name</th>
              <th>Created At</th>
              <th>Updated At</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {dashboards.map(dashboard => (
              <tr key={dashboard.id}>
                <td>{dashboard.id}</td>
                <td>
                  <Link 
                    to={`/dashboards/${dashboard.id}`}
                    style={{color: '#034955', textDecoration: 'none', fontWeight: '500'}}
                    onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                    onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                  >
                    {dashboard.name}
                  </Link>
                </td>
                <td>{new Date(dashboard.created_at).toLocaleDateString()}</td>
                <td>{new Date(dashboard.updated_at).toLocaleDateString()}</td>
                <td>
                  <Link 
                    to={`/dashboards/${dashboard.id}`}
                    className="btn btn-primary btn-small"
                    style={{marginRight: '0.5rem'}}
                  >
                    Details
                  </Link>
                  <button 
                    className="btn btn-secondary btn-small" 
                    onClick={() => handleEdit(dashboard)}
                    style={{marginRight: '0.5rem'}}
                  >
                    Edit
                  </button>
                  <button 
                    className="btn btn-danger btn-small" 
                    onClick={() => handleDelete(dashboard.id)}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {dashboards.length === 0 && (
          <div style={{padding: '2rem', textAlign: 'center', color: '#6c757d'}}>
            No dashboards found. Add one to get started!
          </div>
        )}
      </div>
    </div>
  );
};

export default DashboardList;

